import DeleteIcon from '../components/DeleteIcon';
import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import './FichaLevantamento.css';

const MAX_PHOTOS = 10;

const FichaLevantamento = () => {
    const { user } = useAuth();
    const navigate = useNavigate();

    const [obras, setObras] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [selectedObraId, setSelectedObraId] = useState('');
    const [selectedTemplate, setSelectedTemplate] = useState(null);

    const [formData, setFormData] = useState({});
    const [fotos, setFotos] = useState([]);

    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const [obrasRes, templatesRes] = await Promise.all([
                    api.get('/obras'),
                    api.get('/fichas-levantamento/templates')
                ]);

                const obrasList = obrasRes.data.obras || obrasRes.data;
                const activeObras = obrasList.filter(o => o.estado !== 'Concluida' && o.estado !== 'Cancelada');
                setObras(activeObras.length > 0 ? activeObras : obrasList);

                const temps = templatesRes.data || [];
                setTemplates(temps);
                if (temps.length > 0) {
                    selectTemplate(temps[0]);
                }
            } catch (err) {
                console.error("Erro a carregar dados:", err);
                setMessage({ type: 'error', text: 'Erro ao carregar obras e templates.' });
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, []);

    const selectTemplate = (template) => {
        if (!template) return;
        const clonedTemplate = JSON.parse(JSON.stringify(template));

        // Migrações no frontend para lidar com templates antigos
        if (clonedTemplate.sections) {
            clonedTemplate.sections.forEach(sec => {
                if (sec.questions) {
                    sec.questions.forEach(q => {
                        if (q.type) {
                            q.show_sn = q.type.includes('sn_');
                            q.show_quant = q.type.includes('_qtd_');
                            q.show_un = q.type.includes('_unid_');
                            q.show_m2 = q.type.includes('_m2_');
                            q.show_ml = q.type.includes('_ml_');
                            q.show_obs = q.type.includes('_obs') || q.type === 'obs';
                            if (q.type.startsWith('number_')) q.show_quant = true;
                            q.show_largura = false;
                            q.show_altura = false;
                            q.show_dropdown = false;
                            q.dropdown_options = '';
                            q.is_multiplier = false;
                            delete q.type;
                        }
                    });
                }
            });
        }

        setSelectedTemplate(clonedTemplate);
        const initialData = {};
        if (clonedTemplate.sections) {
            clonedTemplate.sections.forEach(sec => {
                if (sec.questions) {
                    sec.questions.forEach(q => {
                        if (q.is_multiplier) {
                            initialData[q.id] = { multiplier_count: '', rows: [] };
                        } else {
                            initialData[q.id] = { sn: '', val: '', quant: '', un: '', m2: '', ml: '', largura: '', altura: '', obs: '', dropdown: '' };
                        }
                    });
                }
            });
        }
        setFormData(initialData);
    };

    const handleFormChange = (qId, field, value) => {
        setFormData(prev => ({
            ...prev,
            [qId]: {
                ...prev[qId],
                [field]: value
            }
        }));
    };

    const handleMultiplierCountChange = (qId, countStr) => {
        const count = parseInt(countStr) || 0;
        setFormData(prev => {
            const currentRows = prev[qId]?.rows || [];
            let newRows = [...currentRows];
            if (count > currentRows.length) {
                for (let i = currentRows.length; i < count; i++) {
                    newRows.push({ sn: '', quant: '', un: '', m2: '', ml: '', largura: '', altura: '', obs: '', val: '', dropdown: '' });
                }
            } else if (count < currentRows.length) {
                newRows = newRows.slice(0, count);
            }
            return {
                ...prev,
                [qId]: {
                    ...prev[qId],
                    multiplier_count: countStr,
                    rows: newRows
                }
            };
        });
    };

    const handleMultiplierRowChange = (qId, rIdx, field, value) => {
        setFormData(prev => {
            const currentRows = [...(prev[qId]?.rows || [])];
            currentRows[rIdx] = { ...currentRows[rIdx], [field]: value };
            return {
                ...prev,
                [qId]: {
                    ...prev[qId],
                    rows: currentRows
                }
            };
        });
    };

    const handlePhotoUpload = (e) => {
        const files = Array.from(e.target.files);
        if (fotos.length + files.length > MAX_PHOTOS) {
            alert(`Pode adicionar no máximo ${MAX_PHOTOS} fotos.`);
            return;
        }
        files.forEach(file => {
            const reader = new FileReader();
            reader.onloadend = () => {
                setFotos(prev => [...prev, reader.result]);
            };
            reader.readAsDataURL(file);
        });
    };

    const removePhoto = (indexToRemove) => {
        setFotos(prev => prev.filter((_, idx) => idx !== indexToRemove));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedObraId || !selectedTemplate) {
            setMessage({ type: 'error', text: 'Por favor, selecione a Obra.' });
            return;
        }

        setIsSubmitting(true);
        setMessage({ type: '', text: '' });

        try {
            const payload = {
                obra_id: selectedObraId,
                template_id: selectedTemplate.id,
                dados: formData,
                fotos: fotos,
                preenchido_por: user?.id
            };

            await api.post('/fichas-levantamento', payload);
            setMessage({ type: 'success', text: 'Ficha submetida com sucesso!' });

            // Go back or reset
            setTimeout(() => {
                navigate(user?.perfil_mobile ? '/mobile-dashboard' : '/obras');
            }, 2000);

        } catch (err) {
            console.error(err);
            setMessage({ type: 'error', text: 'Erro ao submeter a ficha. Tente novamente.' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderRowInputs = (q, rowData, onChange) => {
        return (
            <div className="fl-inputs-row" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', width: '100%' }}>
                {q.show_sn && (
                    <div className="fl-sn-toggle">
                        <button type="button" className={`fl-btn ${rowData.sn === 'S' ? 'active-s' : ''}`} onClick={() => onChange('sn', 'S')}>S</button>
                        <button type="button" className={`fl-btn ${rowData.sn === 'N' ? 'active-n' : ''}`} onClick={() => onChange('sn', 'N')}>N</button>
                    </div>
                )}

                {q.show_quant && <input type="number" placeholder="Quant." className="fl-val-input" style={{ width: '80px' }} value={rowData.quant || rowData.val || ''} onChange={(e) => onChange('quant', e.target.value)} />}
                {q.show_un && <input type="number" placeholder="Un." className="fl-val-input" style={{ width: '80px' }} value={rowData.un || ''} onChange={(e) => onChange('un', e.target.value)} />}
                {q.show_m2 && <input type="number" placeholder="m²" className="fl-val-input" style={{ width: '80px' }} value={rowData.m2 || ''} onChange={(e) => onChange('m2', e.target.value)} />}
                {q.show_ml && <input type="number" placeholder="m/L" className="fl-val-input" style={{ width: '80px' }} value={rowData.ml || ''} onChange={(e) => onChange('ml', e.target.value)} />}
                {q.show_largura && <input type="number" placeholder="Larg.(m)" className="fl-val-input" style={{ width: '90px' }} value={rowData.largura || ''} onChange={(e) => onChange('largura', e.target.value)} />}
                {q.show_altura && <input type="number" placeholder="Alt.(m)" className="fl-val-input" style={{ width: '90px' }} value={rowData.altura || ''} onChange={(e) => onChange('altura', e.target.value)} />}

                {q.show_dropdown && (
                    <select className="fl-val-input" value={rowData.dropdown || ''} onChange={(e) => onChange('dropdown', e.target.value)}>
                        <option value="">Selecione...</option>
                        {q.dropdown_options && q.dropdown_options.split(',').map(opt => opt.trim()).filter(Boolean).map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                        ))}
                    </select>
                )}

                {q.show_obs && <input type="text" placeholder="Observações..." className="fl-obs-input" style={{ flex: 1, minWidth: '150px' }} value={rowData.obs || ''} onChange={(e) => onChange('obs', e.target.value)} />}
            </div>
        );
    };

    const renderInputTypes = (q) => {
        const dt = formData[q.id] || {};

        if (q.is_multiplier) {
            return (
                <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <input
                            type="number"
                            min="0"
                            placeholder="Qtd de sub-itens..."
                            className="fl-val-input"
                            style={{ width: '130px' }}
                            value={dt.multiplier_count || ''}
                            onChange={(e) => handleMultiplierCountChange(q.id, e.target.value)}
                        />
                    </div>
                    {dt.rows && dt.rows.length > 0 && (
                        <div style={{ paddingLeft: '15px', borderLeft: '3px solid #cbd5e0', display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '5px' }}>
                            {dt.rows.map((row, rIdx) => (
                                <div key={rIdx} style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                    <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#718096', width: '25px' }}>#{rIdx + 1}</span>
                                    {renderRowInputs(q, row, (field, val) => handleMultiplierRowChange(q.id, rIdx, field, val))}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            );
        } else {
            return renderRowInputs(q, dt, (field, val) => handleFormChange(q.id, field, val));
        }
    };

    return (
        <div className="ficha-levantamento-container">
            <div className="top-bar">
                <div className="top-bar-left">
                    <div className="top-bar-title">
                        <h1>📋 Ficha de Levantamento</h1>
                    </div>
                </div>
                <div className="top-bar-actions">
                    <button className="btn-secondary" onClick={() => navigate(-1)}>
                        ← Voltar
                    </button>
                </div>
            </div>

            <div className="page-content" style={{ padding: '20px' }}>
                <div className="fl-content-wrapper">
                    {message.text && (
                        <div className={`fl-message ${message.type}`}>
                            {message.text}
                        </div>
                    )}

                    {!isLoading && selectedTemplate ? (
                        <form className="fl-form" onSubmit={handleSubmit}>
                            <div className="fl-form-group">
                                <label>Obra*</label>
                                <select
                                    value={selectedObraId}
                                    onChange={(e) => setSelectedObraId(e.target.value)}
                                    required
                                >
                                    <option value="">Selecione uma Obra pré carregada</option>
                                    {obras.map(o => (
                                        <option key={o.id} value={o.id}>{o.posicao || o.nome}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="fl-form-group">
                                <label>Questionário</label>
                                <select
                                    value={selectedTemplate?.id || ''}
                                    onChange={(e) => selectTemplate(templates.find(t => t.id == e.target.value))}
                                >
                                    {templates.map(t => (
                                        <option key={t.id} value={t.id}>{t.nome}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="fl-checklist-section">
                                {selectedTemplate.sections?.map((section, sIdx) => (
                                    <div key={sIdx} className="fl-section-block">
                                        <h4 className="fl-section-title">{section.title}</h4>
                                        <div className="fl-questions-list">
                                            {section.questions?.map((q) => (
                                                <div key={q.id} className="fl-question-row">
                                                    <div className="fl-question-label">{q.label}</div>
                                                    <div className="fl-question-inputs">
                                                        {renderInputTypes(q)}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="fl-photos-section">
                                <label>Registo Fotográfico (Max {MAX_PHOTOS})</label>
                                <div className="photo-upload-wrapper">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        capture="environment"
                                        multiple
                                        onChange={handlePhotoUpload}
                                        disabled={fotos.length >= MAX_PHOTOS}
                                        id="photo-upload-fl"
                                    />
                                    <label htmlFor="photo-upload-fl" className={`upload-btn ${fotos.length >= MAX_PHOTOS ? 'disabled' : ''}`}>
                                        📷 Tirar / Anexar Fotos
                                    </label>
                                </div>

                                {fotos.length > 0 && (
                                    <div className="photo-preview-grid">
                                        {fotos.map((src, index) => (
                                            <div key={index} className="photo-thumbnail">
                                                <img src={src} alt={`Foto ${index + 1}`} />
                                                <button type="button" className="remove-photo-btn" onClick={() => removePhoto(index)} aria-label="Apagar" title="Apagar"><DeleteIcon /></button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="fl-actions">
                                <button type="submit" className="fl-submit-btn" disabled={isSubmitting}>
                                    {isSubmitting ? 'A submeter...' : 'Guardar Ficha'}
                                </button>
                            </div>
                        </form>
                    ) : (
                        <p>A carregar formulário...</p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FichaLevantamento;
